import React from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { FormInput } from "../../../../components/ui";
import { EMAILS_LIST_VALIDATION_SCHEMA } from "../../../../utils/validation";
import { FUNDED_COMPLIANCE_API_URLS, FundedComplianceAPI } from "../../../../api/fundedCompliance";
import { IOnError } from "../../../Affiliates/components/AddNewAffiliate";

interface IProps {
  groupId?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const getDialogText = (groupId: number | undefined) => {
  const action = groupId ? "addInTrader" : "createTrader";
  const dialogData = {
    addInTrader: {
      dialogTitle: "Add account(s) to trader",
      dialogContentText: "Please enter the emails of the accounts you want to add to the trader",
    },
    createTrader: {
      dialogTitle: "Create trader",
      dialogContentText: "Please enter the emails of the accounts you want to add to the trader",
    },
  };

  return dialogData[action];
};

const ExtendOrCreateTrader = ({ groupId, isOpen, setIsOpen }: IProps): React.ReactElement => {
  const queryClient = useQueryClient();
  const { dialogTitle, dialogContentText } = getDialogText(groupId);

  const handleSubmit = useMutation({
    mutationFn: async (data: {
      values: { emails: string };
      rest: FormikHelpers<{ emails: string }>;
    }) => {
      const { values, rest } = data;

      const arrayValues = values.emails.trim().split(/[\s,]+/);
      try {
        if (groupId) {
          await FundedComplianceAPI.addInOrCreateGroupRequest({
            groupId,
            emails: arrayValues,
          });
        } else {
          await FundedComplianceAPI.addInOrCreateGroupRequest({
            emails: arrayValues,
          });
        }

        setIsOpen(false);
        queryClient.invalidateQueries({
          queryKey: [FUNDED_COMPLIANCE_API_URLS.tradersDbList, "tradersDbList"],
        });

        return;
      } catch (error) {
        const e = error as IOnError;
        rest.setErrors({ emails: e.response.data.message });
      }
    },
    mutationKey: [FUNDED_COMPLIANCE_API_URLS.tradersDbList, "addInOrCreateGroup"],
  });

  return (
    <div>
      <Dialog open={isOpen} fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>

        <DialogContent>
          <DialogContentText>{dialogContentText}</DialogContentText>
        </DialogContent>

        <Formik
          validationSchema={EMAILS_LIST_VALIDATION_SCHEMA}
          initialValues={{
            emails: "",
          }}
          validateOnChange
          onSubmit={(values, rest) => {
            handleSubmit.mutate({ values, rest });
          }}
        >
          {({ isValid, dirty }) => {
            return (
              <Box sx={{ px: "24px" }}>
                <Form>
                  <Field
                    name="emails"
                    component={FormInput}
                    placeholder="Comma or space separated list of emails"
                    label="Emails"
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                  />

                  <DialogActions sx={{ mt: "16px" }}>
                    <Button onClick={() => setIsOpen(false)} size="small">
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      size="small"
                      color="secondary"
                      autoFocus
                      disabled={!isValid || !dirty}
                    >
                      Ok
                    </Button>
                  </DialogActions>
                </Form>
              </Box>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ExtendOrCreateTrader;
